import type { ReactNode } from "react"

import type { ActivityMessage } from "@/api/models"
import type { TFunction } from "i18next"

import {
  BookOpen,
  Brain,
  Calendar,
  Code2,
  FileSearch,
  FileText,
  FolderSearch,
  Globe,
  ImageIcon,
  Lightbulb,
  List,
  Mail,
  MessageSquare,
  MousePointerClick,
  Search,
  Send,
  Sparkles,
  Wrench,
} from "@/components/icons"
import { M365Icon } from "@/features/ai-explorer/components/m365-icon"
import { translateActivityMessage } from "@/utils/activity-message-translator"

import {
  ActivityType,
  type ImageGeneratedResult,
  type SearchResult,
  type WebSearchResult,
} from "../../types/activity-message-types"

const getM365IconFromSources = (sources: string[]): ReactNode => {
  const sourceStr = sources.join(" ").toLowerCase()

  if (sourceStr.includes("email") || sourceStr.includes("inbox")) {
    return <Mail className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />
  }
  if (sourceStr.includes("calendar")) {
    return <Calendar className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />
  }
  if (sourceStr.includes("teams") || sourceStr.includes("chat") || sourceStr.includes("meeting")) {
    return <MessageSquare className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />
  }
  if (sourceStr.includes("file") || sourceStr.includes("document")) {
    return <FileText className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />
  }

  return <M365Icon className="text-primary/40 shrink-0" />
}

export const generateMarkdownContent = (activity: ActivityMessage, t: TFunction): string => {
  const activityType = activity.activity_type || "message"
  const shouldBold = activityType !== "message"
  // Trimmed: CommonMark does not close `**` after whitespace, so a label
  // ending in a space rendered as literal asterisks ("**memory_notes: **").
  let translatedMessage = translateActivityMessage(activity, t).trim()
  if (!translatedMessage) return ""

  translatedMessage = translatedMessage.replace(/\*{4}/g, "**")

  const alreadyBold = translatedMessage.startsWith("**")
  const boldDelimiter = shouldBold && !alreadyBold ? "**" : ""

  if (shouldBold && !alreadyBold && translatedMessage.startsWith("- ")) {
    const contentWithoutDash = translatedMessage.slice(2)
    return `- ${boldDelimiter}${contentWithoutDash}${boldDelimiter}`
  }

  return `${boldDelimiter}${translatedMessage}${boldDelimiter}`
}

export const getActivityIcon = (activity: ActivityMessage): ReactNode => {
  const key =
    typeof activity.details?.translation_key === "string"
      ? (activity.details.translation_key as string)
      : activity.activity_type || "message"

  if (key === "microsoft_365_search" || key === "microsoft_365_search_started") {
    const sources = activity.details?.sources
    if (Array.isArray(sources) && sources.length > 0) {
      return getM365IconFromSources(sources as string[])
    }
    return <M365Icon className="text-primary/40 shrink-0" />
  }

  const iconMap: Record<string, ReactNode> = {
    research: <Brain className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />,
    web_search: <Globe className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />,
    web_search_started: <Globe className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />,
    web_search_results: <List className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />,
    web_scrape: <MousePointerClick className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />,
    web_scrape_started: (
      <MousePointerClick className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />
    ),
    browse_results: (
      <MousePointerClick className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />
    ),
    search_query: <Search className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />,
    search_results: <FolderSearch className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />,
    tools: <Wrench className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />,
    tools_selected: <Wrench className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />,
    files_analyzing: <FileText className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />,
    library_search: <BookOpen className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />,
    library_search_started: (
      <BookOpen className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />
    ),
    file_search: <FileSearch className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />,
    file_search_started: (
      <FileSearch className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />
    ),
    doc_creator_started: <FileText className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />,
    doc_creator_completed: (
      <FileText className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />
    ),
    doc_updater_started: <FileText className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />,
    doc_updater_completed: (
      <FileText className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />
    ),
    doc_content_reader_started: (
      <FileSearch className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />
    ),
    doc_content_reader_completed: (
      <FileSearch className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />
    ),
    doc_internal_search_started: (
      <Search className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />
    ),
    doc_internal_search_completed: (
      <Search className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />
    ),
    navigator_search_started: (
      <BookOpen className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />
    ),
    navigator_search_completed_singular: (
      <BookOpen className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />
    ),
    navigator_search_completed_plural: (
      <BookOpen className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />
    ),
    web_search_results_found: (
      <Globe className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />
    ),
    code_interpreter_started: (
      <Code2 className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />
    ),
    code_executed: <Code2 className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />,
    code_output: <Code2 className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />,
    image_generated: <ImageIcon className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />,
    memory_saved: <Lightbulb className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />,
    memory_suggested: <Lightbulb className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />,
    memory_already_saved: (
      <Lightbulb className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />
    ),
    // Explorer Auto model+effort routing decision (#2436) — same icon as the
    // chat model-selector pill, so this reads as "a model choice was made."
    model_routed: <Sparkles className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />,
    // Client-injected steer confirmation (#2469) — same Send + primary tint as
    // UserMessage steered provenance and ComposerQueueBar Steer.
    steer_applied: <Send className="text-primary size-4 shrink-0" strokeWidth={1.5} />,
  }

  return iconMap[key] || <Brain className="text-primary/40 size-4 shrink-0" strokeWidth={1.5} />
}

export const convertWebSearchResults = (activity: ActivityMessage): WebSearchResult => {
  return {
    type: ActivityType.WEB_SEARCH_RESULTS,
    results: Array.isArray(activity.details?.results)
      ? (activity.details.results as { title: string; url: string }[])
      : [],
  }
}

export const convertSearchResults = (activity: ActivityMessage): SearchResult => {
  return {
    type: ActivityType.SEARCH_RESULTS,
    results: Array.isArray(activity.details?.results)
      ? (activity.details.results as { title: string; library: string; snippet?: string }[])
      : [],
  }
}

export const convertImageGeneratedResult = (activity: ActivityMessage): ImageGeneratedResult => {
  return {
    type: ActivityType.IMAGE_GENERATED,
    job_id: activity.details?.job_id as string | undefined,
    image_url: (activity.details?.image_url as string) || "",
    revised_prompt: activity.details?.revised_prompt as string | undefined,
    prompt: (activity.details?.prompt as string) || "",
    is_edit: activity.details?.is_edit as boolean | undefined,
    progress: activity.details?.progress as number | undefined,
    partial: activity.details?.partial as boolean | undefined,
    session_file: activity.details?.session_file as string | undefined,
    model_id: activity.details?.model_id as string | undefined,
    model_label: activity.details?.model_label as string | undefined,
  }
}
